import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useCustomFetch } from '~/composables/useCustomFetch.js'

export const useSearchDialogStore = defineStore('searchDialog', () => {
  // State
  const isVisible = ref(false)
  const searchTerm = ref('')
  const searchResults = ref({
    publications: [],
    artists: [],
    producers: [],
    locations: []
  })
  const isSearching = ref(false)
  const showSearchResults = ref(false)
  const activeResultIndex = ref(-1)
  const allFlatResults = ref([])
  const recentSearches = ref([])

  // Load recent searches from localStorage (only in client-side)
  if (process.client) {
    try {
      const savedSearches = localStorage.getItem('recentSearches')
      if (savedSearches) {
        recentSearches.value = JSON.parse(savedSearches).slice(0, 5)
      }
    } catch (error) {
      console.error('Error al cargar búsquedas recientes:', error)
    }
  }

  // Actions
  function showDialog() {
    isVisible.value = true
  }

  function hideDialog() {
    isVisible.value = false
    resetSearch()
  }

  function resetSearch() {
    searchTerm.value = ''
    showSearchResults.value = false
  }

  async function performSearch() {
    if (!searchTerm.value || searchTerm.value.length < 2) {
      searchResults.value = {
        publications: [],
        artists: [],
        producers: [],
        locations: []
      }
      return
    }

    isSearching.value = true
    try {
      const { data } = await useCustomFetch(`search`, {
        params: {
          q: searchTerm.value,
          rowsPerPage: 5
        },
      })

      if (data.value) {
        searchResults.value = data.value
        updateFlatResults()
      }
    } catch (error) {
      console.error('Error al buscar:', error)
    } finally {
      isSearching.value = false
    }
  }

  function updateFlatResults() {
    allFlatResults.value = [
      ...searchResults.value.publications.map(item => ({ ...item, type: 'publication' })),
      ...searchResults.value.artists.map(item => ({ ...item, type: 'artist' })),
      ...searchResults.value.producers.map(item => ({ ...item, type: 'producer' })),
      ...searchResults.value.locations.map(item => ({ ...item, type: 'location' }))
    ]
    activeResultIndex.value = -1
  }

  function saveToRecentSearches(term) {
    if (!term) return

    const index = recentSearches.value.indexOf(term)
    if (index > -1) {
      recentSearches.value.splice(index, 1)
    }

    recentSearches.value.unshift(term)
    if (recentSearches.value.length > 5) {
      recentSearches.value.pop()
    }

    if (process.client) {
      try {
        localStorage.setItem('recentSearches', JSON.stringify(recentSearches.value))
      } catch (error) {
        console.error('Error al guardar búsquedas recientes:', error)
      }
    }
  }

  function clearRecentSearches() {
    recentSearches.value = []
    if (process.client) {
      try {
        localStorage.removeItem('recentSearches')
      } catch (error) {
        console.error('Error al limpiar búsquedas recientes:', error)
      }
    }
  }

  function selectRecentSearch(term) {
    searchTerm.value = term
    performSearch()
  }

  return {
    // State
    isVisible,
    searchTerm,
    searchResults,
    isSearching,
    showSearchResults,
    activeResultIndex,
    allFlatResults,
    recentSearches,

    // Getters
    totalResults: () => {
      return searchResults.value.publications.length +
             searchResults.value.artists.length +
             searchResults.value.producers.length +
             searchResults.value.locations.length
    },
    hasResults: () => {
      return searchResults.value.publications.length +
             searchResults.value.artists.length +
             searchResults.value.producers.length +
             searchResults.value.locations.length > 0
    },

    // Actions
    showDialog,
    hideDialog,
    resetSearch,
    performSearch,
    updateFlatResults,
    saveToRecentSearches,
    clearRecentSearches,
    selectRecentSearch
  }
})
